/**
 * What the Events tab is currently showing: the search, which sections and
 * topics are open, and whether retired rows are listed.
 *
 * It belongs to the screen rather than the tab because the error state replaces
 * everything under the header, and a tab holding this itself would come back
 * from "Try again" with the search cleared and every section shut.
 *
 * @link ./NotificationEventsTab for the view it drives.
 */
import { useCallback, useMemo, useState } from 'react';
import { groupByTopic } from './eventGroups';
import { canTurnOn } from './eventState';
import type { EventSideFilter } from './eventReach';
import type { SetupCatalogue, SetupDomain, SetupEvent } from './types';

const matches = (event: SetupEvent, term: string): boolean => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    event.name.toLowerCase().includes(needle)
    || event.key.toLowerCase().includes(needle)
    || event.subdomainLabel.toLowerCase().includes(needle)
    || event.description.toLowerCase().includes(needle)
  );
};

/** A both-sides event is in each side's list: it does reach that side. */
const reachesSide = (event: SetupEvent, side: EventSideFilter): boolean =>
  side === 'all' || event.reaches === side || event.reaches === 'both';

/** One section with the rows the search leaves it, retired ones included. */
export interface EventsViewSection extends SetupDomain {
  visible: SetupEvent[];
}

export interface EventsView {
  search: string;
  setSearch: (search: string) => void;
  /** Narrows the sections to one side of the portal, alongside the search. */
  side: EventSideFilter;
  setSide: (side: EventSideFilter) => void;
  /** Live events each side lists, for the figures on its button. */
  sideCounts: Record<'customer' | 'supplier', number>;
  sections: EventsViewSection[];
  /** Retired rows the current search matches — the figure on the toggle. */
  retiredCount: number;
  showRetired: boolean;
  toggleRetired: () => void;
  isOpen: (domain: string) => boolean;
  setOpen: (domain: string, open: boolean) => void;
  isTopicOpen: (topic: string) => boolean;
  setTopicOpen: (topic: string, open: boolean) => void;
}

/**
 * @example
 * const view = useEventsView(setup.catalogue);
 * view.setSearch('dispute');
 * view.sections.map((section) => section.visible);
 *
 * @param catalogue - the parsed catalogue, or `null` before the first load.
 * @returns the filtered sections plus the search, side, open and retired state.
 */
export function useEventsView(catalogue: SetupCatalogue | null): EventsView {
  const [search, setSearch] = useState('');
  const [side, setSide] = useState<EventSideFilter>('all');
  const [openDomains, setOpenDomains] = useState<Record<string, boolean>>({});
  const [openTopics, setOpenTopics] = useState<Record<string, boolean>>({});
  const [showRetired, setShowRetired] = useState(false);

  const sections = useMemo(
    () =>
      (catalogue?.domains ?? []).map((section) => ({
        ...section,
        visible: section.events.filter(
          (event) => matches(event, search) && reachesSide(event, side),
        ),
      })),
    [catalogue, search, side],
  );

  const sideCounts = useMemo(() => {
    const live = (catalogue?.domains ?? []).flatMap((section) => section.events.filter(canTurnOn));
    const count = (choice: EventSideFilter): number =>
      live.filter((event) => reachesSide(event, choice)).length;
    return { customer: count('customer'), supplier: count('supplier') };
  }, [catalogue]);

  const retiredCount = useMemo(
    () =>
      sections.reduce(
        (count, section) =>
          count + section.visible.filter((event) => !canTurnOn(event)).length,
        0,
      ),
    [sections],
  );

  // A search implies "show me the matches": sections and topics open themselves
  // while one is active. The admin's own choice still wins, and the next search
  // starts by showing every match again.
  const searching = search.trim() !== '';
  const isOpen = useCallback(
    (domain: string) => openDomains[domain] ?? searching,
    [openDomains, searching],
  );

  const setOpen = useCallback((domain: string, open: boolean) => {
    setOpenDomains((current) => ({ ...current, [domain]: open }));
  }, []);

  const isTopicOpen = useCallback(
    (topic: string) => openTopics[topic] ?? searching,
    [openTopics, searching],
  );

  const changeSearch = useCallback((next: string) => {
    const keepOpen = (current: Record<string, boolean>) =>
      Object.fromEntries(Object.entries(current).filter(([, open]) => open));
    setSearch(next);
    setOpenDomains(keepOpen);
    setOpenTopics(keepOpen);
  }, []);

  const setTopicOpen = useCallback((topic: string, open: boolean) => {
    setOpenTopics((current) => ({ ...current, [topic]: open }));
  }, []);

  const toggleRetired = useCallback(() => {
    const next = !showRetired;
    setShowRetired(next);
    if (!next) return;
    // Every section and topic starts shut, so without this the button appears
    // to do nothing at all. Everything holding a retired row opens, matched by
    // the search or not, so the count is still what is listed once it is cleared.
    const holding = sections.filter((section) =>
      section.events.some((event) => !canTurnOn(event)),
    );
    setOpenDomains((current) => {
      const copy = { ...current };
      for (const section of holding) copy[section.domain] = true;
      return copy;
    });
    setOpenTopics((current) => {
      const copy = { ...current };
      for (const section of holding) {
        const retired = section.events.filter((event) => !canTurnOn(event));
        for (const topic of groupByTopic(retired)) copy[topic.key] = true;
      }
      return copy;
    });
  }, [sections, showRetired]);

  return {
    search,
    setSearch: changeSearch,
    side,
    setSide,
    sideCounts,
    sections,
    retiredCount,
    showRetired,
    toggleRetired,
    isOpen,
    setOpen,
    isTopicOpen,
    setTopicOpen,
  };
}
